import React, { useState } from 'react';
import { DollarSign, Home, TrendingUp, Wallet, Calculator, ChevronRight, Target, AlertCircle, CheckCircle, Menu, X } from 'lucide-react';

const ModernSecurityCalculator = () => {
  const [showSolution, setShowSolution] = useState(false);
  const [selectedBackground, setSelectedBackground] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('income');
  
  const [incomeData, setIncomeData] = useState({
    annualIncome: 75000,
    yearsToReplace: 10,
    inflationRate: 3
  });
  
  const [immediateData, setImmediateData] = useState({
    funeralCosts: 15000,
    medicalBills: 5000,
    creditCardDebt: 8000,
    otherDebts: 12000
  });
  
  const [futureData, setFutureData] = useState({
    mortgage: 250000,
    educationCosts: 100000,
    emergencyFund: 25000,
    other: 0
  });
  
  const [resourcesData, setResourcesData] = useState({
    existingLifeInsurance: 50000,
    savings: 15000,
    investments: 30000,
    otherAssets: 10000
  });

  const backgrounds = [
    { name: "Midnight Blue", bg: "from-slate-900 via-blue-900 to-indigo-900", light: false },
    { name: "Deep Ocean", bg: "from-blue-950 via-cyan-900 to-teal-900", light: false },
    { name: "Steel Gray", bg: "from-gray-950 via-slate-900 to-zinc-900", light: false },
    { name: "Classic White", bg: "from-gray-100 via-white to-gray-50", light: true },
    { name: "Arctic Light", bg: "from-sky-100 via-cyan-50 to-blue-100", light: true }
  ];

  const currentBg = backgrounds[selectedBackground] || backgrounds[0];
  const isLight = currentBg.light;

  const calculateIncomeReplacement = () => {
    const { annualIncome, yearsToReplace, inflationRate } = incomeData;
    return annualIncome * yearsToReplace * (1 + inflationRate / 100);
  };

  const calculateImmediateNeeds = () => {
    return Object.values(immediateData).reduce((sum, value) => sum + value, 0);
  };

  const calculateFutureNeeds = () => {
    return Object.values(futureData).reduce((sum, value) => sum + value, 0);
  };

  const calculateCurrentResources = () => {
    return Object.values(resourcesData).reduce((sum, value) => sum + value, 0);
  };

  const totalNeeds = calculateIncomeReplacement() + calculateImmediateNeeds() + calculateFutureNeeds();
  const totalResources = calculateCurrentResources();
  const insuranceGap = Math.max(0, totalNeeds - totalResources);
  const coveragePercent = (totalResources / totalNeeds) * 100;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleInputChange = (setData, field, value) => {
    const numValue = parseFloat(value) || 0;
    setData(prev => ({ ...prev, [field]: numValue }));
  };

  const InputField = ({ label, value, onChange, prefix = '$', suffix = '' }) => (
    <div className="mb-3">
      <label className={`block text-xs font-medium mb-1 ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={(e) => e.target.select()}
          className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all pl-7 ${isLight ? 'bg-gray-100 border-gray-300 text-gray-800' : 'bg-white/5 border-white/10 text-white'}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">{suffix}</span>}
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen bg-gradient-to-br ${currentBg.bg} flex`}>
      
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className={`fixed top-4 left-4 z-50 backdrop-blur-sm border p-3 rounded-xl transition-all duration-300 shadow-lg ${isLight ? 'bg-gray-900/10 border-gray-300 hover:bg-gray-900/20' : 'bg-white/10 border-white/20 hover:bg-white/20'}`}
      >
        {sidebarOpen ? <X className={`w-6 h-6 ${isLight ? 'text-gray-800' : 'text-white'}`} /> : <Menu className={`w-6 h-6 ${isLight ? 'text-gray-800' : 'text-white'}`} />}
      </button>

      <div className={`fixed left-0 top-16 bottom-0 backdrop-blur-xl border-r transition-all duration-300 z-40 overflow-hidden ${isLight ? 'bg-white/70 border-gray-200' : 'bg-black/40 border-white/10'} ${sidebarOpen ? 'w-96 translate-x-0' : 'w-0 -translate-x-full'}`}>
        {sidebarOpen && (
          <div className="p-6 pt-6 pb-6 h-full flex flex-col w-96">
          
          <div className="grid grid-cols-2 gap-2 mb-6 flex-shrink-0">
            <button
              onClick={() => setActiveTab('income')}
              className={`flex items-center justify-center p-3 rounded-lg transition-all duration-200 ${
                activeTab === 'income' 
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white' 
                  : isLight ? 'bg-gray-200/50 text-gray-600 hover:bg-gray-200' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <DollarSign className="w-4 h-4 mr-2" />
              <span className="text-sm font-semibold">Income</span>
            </button>
            
            <button
              onClick={() => setActiveTab('immediate')}
              className={`flex items-center justify-center p-3 rounded-lg transition-all duration-200 ${
                activeTab === 'immediate' 
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' 
                  : isLight ? 'bg-gray-200/50 text-gray-600 hover:bg-gray-200' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Home className="w-4 h-4 mr-2" />
              <span className="text-sm font-semibold">Immediate</span>
            </button>
            
            <button
              onClick={() => setActiveTab('future')}
              className={`flex items-center justify-center p-3 rounded-lg transition-all duration-200 ${
                activeTab === 'future' 
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                  : isLight ? 'bg-gray-200/50 text-gray-600 hover:bg-gray-200' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              <span className="text-sm font-semibold">Future</span>
            </button>
            
            <button
              onClick={() => setActiveTab('resources')}
              className={`flex items-center justify-center p-3 rounded-lg transition-all duration-200 ${
                activeTab === 'resources' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' 
                  : isLight ? 'bg-gray-200/50 text-gray-600 hover:bg-gray-200' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Wallet className="w-4 h-4 mr-2" />
              <span className="text-sm font-semibold">Resources</span>
            </button>
          </div>

          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto pr-2">
            {activeTab === 'income' && (
              <div className={`border rounded-xl p-4 ${isLight ? 'bg-gray-100/50 border-gray-200' : 'bg-white/5 border-white/10'}`}>
                <h3 className={`text-xl font-bold mb-4 ${isLight ? 'text-gray-800' : 'text-white'}`}>Income Replacement</h3>
                <InputField 
                  label="Annual Income" 
                  value={incomeData.annualIncome}
                  onChange={(v) => handleInputChange(setIncomeData, 'annualIncome', v)}
                />
                <InputField 
                  label="Years to Replace" 
                  value={incomeData.yearsToReplace}
                  onChange={(v) => handleInputChange(setIncomeData, 'yearsToReplace', v)}
                  prefix=""
                  suffix="yrs"
                />
                <InputField 
                  label="Inflation Rate" 
                  value={incomeData.inflationRate}
                  onChange={(v) => handleInputChange(setIncomeData, 'inflationRate', v)}
                  prefix=""
                  suffix="%"
                />
                <div className="mt-4 p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>Total Income Need</span>
                    <span className="text-2xl font-bold text-cyan-600">{formatCurrency(calculateIncomeReplacement())}</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'immediate' && (
              <div className={`border rounded-xl p-4 ${isLight ? 'bg-gray-100/50 border-gray-200' : 'bg-white/5 border-white/10'}`}>
                <h3 className={`text-xl font-bold mb-4 ${isLight ? 'text-gray-800' : 'text-white'}`}>Immediate Needs</h3>
                <InputField 
                  label="Funeral Costs" 
                  value={immediateData.funeralCosts}
                  onChange={(v) => handleInputChange(setImmediateData, 'funeralCosts', v)}
                />
                <InputField 
                  label="Medical Bills" 
                  value={immediateData.medicalBills}
                  onChange={(v) => handleInputChange(setImmediateData, 'medicalBills', v)}
                />
                <InputField 
                  label="Credit Card Debt" 
                  value={immediateData.creditCardDebt}
                  onChange={(v) => handleInputChange(setImmediateData, 'creditCardDebt', v)}
                />
                <InputField 
                  label="Other Debts" 
                  value={immediateData.otherDebts}
                  onChange={(v) => handleInputChange(setImmediateData, 'otherDebts', v)}
                />
                <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>Total Immediate Needs</span>
                    <span className="text-2xl font-bold text-green-600">{formatCurrency(calculateImmediateNeeds())}</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'future' && (
              <div className={`border rounded-xl p-4 ${isLight ? 'bg-gray-100/50 border-gray-200' : 'bg-white/5 border-white/10'}`}>
                <h3 className={`text-xl font-bold mb-4 ${isLight ? 'text-gray-800' : 'text-white'}`}>Future Needs</h3>
                <InputField 
                  label="Remaining Mortgage" 
                  value={futureData.mortgage}
                  onChange={(v) => handleInputChange(setFutureData, 'mortgage', v)}
                />
                <InputField 
                  label="Education Costs" 
                  value={futureData.educationCosts}
                  onChange={(v) => handleInputChange(setFutureData, 'educationCosts', v)}
                />
                <InputField 
                  label="Emergency Fund" 
                  value={futureData.emergencyFund}
                  onChange={(v) => handleInputChange(setFutureData, 'emergencyFund', v)}
                />
                <InputField 
                  label="Other" 
                  value={futureData.other}
                  onChange={(v) => handleInputChange(setFutureData, 'other', v)}
                />
                <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>Total Future Needs</span>
                    <span className="text-2xl font-bold text-purple-600">{formatCurrency(calculateFutureNeeds())}</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'resources' && (
              <div className={`border rounded-xl p-4 ${isLight ? 'bg-gray-100/50 border-gray-200' : 'bg-white/5 border-white/10'}`}>
                <h3 className={`text-xl font-bold mb-4 ${isLight ? 'text-gray-800' : 'text-white'}`}>Current Resources</h3>
                <InputField 
                  label="Existing Life Insurance" 
                  value={resourcesData.existingLifeInsurance}
                  onChange={(v) => handleInputChange(setResourcesData, 'existingLifeInsurance', v)}
                />
                <InputField 
                  label="Savings" 
                  value={resourcesData.savings}
                  onChange={(v) => handleInputChange(setResourcesData, 'savings', v)}
                />
                <InputField 
                  label="Investments" 
                  value={resourcesData.investments}
                  onChange={(v) => handleInputChange(setResourcesData, 'investments', v)}
                />
                <InputField 
                  label="Other Assets" 
                  value={resourcesData.otherAssets}
                  onChange={(v) => handleInputChange(setResourcesData, 'otherAssets', v)}
                />
                <div className="mt-4 p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>Total Resources</span>
                    <span className="text-2xl font-bold text-orange-600">{formatCurrency(calculateCurrentResources())}</span>
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>

        </div>
        )}
      </div>

      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-96' : 'ml-0'}`}>
        <div className="p-4">
          
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h1 className={`text-2xl font-bold mb-0.5 ${isLight ? 'text-gray-800' : 'text-white'}`}>
                Family Security Calculator
              </h1>
              <p className={`text-xs ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>Comprehensive financial protection analysis</p>
            </div>
            <select
              value={selectedBackground}
              onChange={(e) => setSelectedBackground(parseInt(e.target.value))}
              className={`border px-3 py-1.5 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${isLight ? 'bg-white border-gray-300 text-gray-800' : 'bg-white/10 border-white/20 text-white'}`}
            >
              {backgrounds.map((bg, index) => (
                <option key={index} value={index} className="bg-gray-900 text-white">{bg.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div className={`backdrop-blur-sm border border-cyan-500/30 rounded-lg p-3 ${isLight ? 'bg-white/50' : 'bg-white/5'}`}>
              <div className={`text-xs mb-0.5 ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>Total Needs</div>
              <div className="text-xl font-bold text-cyan-600">{formatCurrency(totalNeeds)}</div>
            </div>

            <div className={`backdrop-blur-sm border rounded-lg p-3 ${isLight ? 'bg-white/50 border-gray-200' : 'bg-white/5 border-white/10'}`}>
              <div className={`text-xs mb-0.5 ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>Current Resources</div>
              <div className={`text-xl font-bold ${isLight ? 'text-gray-800' : 'text-white'}`}>{formatCurrency(totalResources)}</div>
            </div>

            <div className={`backdrop-blur-sm border rounded-lg p-3 ${isLight ? 'bg-white/50 border-red-500/30' : 'bg-white/5 border-yellow-500/30'}`}>
              <div className={`text-xs mb-0.5 ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>Coverage Gap</div>
              <div className={`text-xl font-bold ${isLight ? 'text-red-600' : 'text-yellow-400'}`}>{formatCurrency(insuranceGap)}</div>
            </div>

            <div className={`backdrop-blur-sm border rounded-lg p-3 ${isLight ? 'bg-white/50 border-gray-200' : 'bg-white/5 border-white/10'}`}>
              <div className={`text-xs mb-0.5 ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>Coverage Ratio</div>
              <div className="flex items-baseline">
                <div className={`text-xl font-bold ${isLight ? 'text-gray-800' : 'text-white'}`}>{coveragePercent.toFixed(1)}%</div>
                {coveragePercent >= 100 ? (
                  <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-yellow-500 ml-2" />
                )}
              </div>
            </div>
          </div>

          <div className={`backdrop-blur-sm border rounded-lg p-3 mb-3 ${isLight ? 'bg-white/50 border-gray-200' : 'bg-white/5 border-white/10'}`}>
            <div className="flex items-center justify-between mb-1">
              <h3 className={`text-sm font-bold ${isLight ? 'text-gray-800' : 'text-white'}`}>Coverage Progress</h3>
              <span className="text-base font-bold text-cyan-600">{coveragePercent.toFixed(1)}%</span>
            </div>
            <div className={`rounded-full h-2 overflow-hidden ${isLight ? 'bg-gray-300' : 'bg-gray-700'}`}>
              <div 
                className="bg-gradient-to-r from-cyan-500 to-blue-500 h-full transition-all duration-500"
                style={{ width: `${Math.min(coveragePercent, 100)}%` }}
              ></div>
            </div>
          </div>

          <div className={`rounded-xl p-4 border ${insuranceGap > 0 ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
            <div className="flex items-start mb-2">
              {insuranceGap > 0 ? (
                <Target className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <h3 className={`text-base font-bold mb-1 ${isLight ? 'text-gray-800' : 'text-white'}`}>
                  {insuranceGap > 0 ? 'Action Required' : 'Well Protected'}
                </h3>
                <p className={`text-xs leading-relaxed ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>
                  {insuranceGap > 0 
                    ? `You need ${formatCurrency(insuranceGap)} in additional life insurance coverage to fully protect your family's financial future.`
                    : 'Your current resources exceed your calculated needs. Your family is well protected financially.'
                  }
                </p>
              </div>
            </div>
            {insuranceGap > 0 && (
              <button
                onClick={() => setShowSolution(true)}
                className="w-full mt-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold py-2 px-4 text-sm rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 shadow-lg flex items-center justify-center group"
              >
                View Detailed Solution
                <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            )}
          </div>

        </div>
      </div>

      {showSolution && (
        <div 
          className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-8 overflow-y-auto"
          onClick={() => setShowSolution(false)}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
          <div 
            className="relative z-10 w-full max-w-2xl my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-slate-900 to-blue-900 border border-white/20 rounded-2xl p-8 shadow-2xl">
              <div className="flex items-center mb-6">
                <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-3 rounded-xl mr-4">
                  <Calculator className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-white">100% Coverage Solution</h2>
                  <p className="text-gray-400">Complete financial protection breakdown</p>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-6 mb-6">
                <div className="text-center">
                  <div className="text-sm text-gray-300 mb-2">Additional Insurance Needed</div>
                  <div className="text-5xl font-bold text-cyan-400 mb-2">{formatCurrency(insuranceGap)}</div>
                  <div className="text-gray-400 text-sm">to achieve 100% family protection</div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center p-4 bg-white/5 rounded-lg border border-white/10">
                  <span className="text-gray-300 font-medium">Total Family Needs</span>
                  <span className="text-white font-bold text-lg">{formatCurrency(totalNeeds)}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-white/5 rounded-lg border border-white/10">
                  <span className="text-gray-300 font-medium">Current Resources</span>
                  <span className="text-white font-bold text-lg">- {formatCurrency(totalResources)}</span>
                </div>
                <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                <div className="flex justify-between items-center p-4 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-lg border border-cyan-500/30">
                  <span className="text-white font-bold">Insurance Gap</span>
                  <span className="text-cyan-400 font-bold text-2xl">{formatCurrency(insuranceGap)}</span>
                </div>
              </div>

              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-300 text-sm">
                    With {formatCurrency(insuranceGap)} in additional coverage, your family will have complete financial security covering all income replacement, immediate expenses, and future obligations.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowSolution(false)}
                className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 border border-white/20"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernSecurityCalculator;